import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { CircleNotch } from '@phosphor-icons/react'

interface GameClass {
  id: string
  name: string
  description: string
  icon: string
  starting_points: number
}

interface EditClassDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  classData: GameClass | null
  onSave: (classData: Omit<GameClass, 'id'> & { id?: string }) => Promise<void>
}

// Emoji picker simples - lista de emojis comuns para RPG
const COMMON_EMOJIS = [
  '⚔️', '🛡️', '🏹', '🔮', '📖', '💎', '🗡️', '🪄', 
  '⚡', '🔥', '❄️', '🌊', '🌪️', '☀️', '🌙', '⭐',
  '🧙', '🧝', '🧛', '🧟', '👑', '💀', '🦇', '🐉',
  '🧪', '🔬', '📊', '🎓', '🏛️', '🎭', '🎨', '🎵'
]

export function EditClassDialog({ open, onOpenChange, classData, onSave }: EditClassDialogProps) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    icon: '⚔️',
    starting_points: 10
  })
  const [isSaving, setIsSaving] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)

  const isEditing = !!classData

  // Preencher form quando editar classe existente
  useEffect(() => {
    if (classData) {
      setFormData({
        id: classData.id,
        name: classData.name,
        description: classData.description,
        icon: classData.icon,
        starting_points: classData.starting_points
      })
    } else {
      // Resetar para criação
      setFormData({
        id: '',
        name: '',
        description: '',
        icon: '⚔️',
        starting_points: 10
      })
    }
  }, [classData, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Gerar ID se for nova classe
    const classId = formData.id || formData.name.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[^a-z0-9]+/g, '_') // Substitui caracteres especiais por _
      .replace(/^_+|_+$/g, '') // Remove _ do início e fim

    setIsSaving(true)
    try {
      await onSave({
        ...(isEditing && { id: formData.id }),
        ...(!isEditing && { id: classId }),
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
        starting_points: formData.starting_points
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Erro ao salvar classe:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Editar Classe' : 'Nova Classe'}
            </DialogTitle>
            <DialogDescription>
              {isEditing 
                ? 'Modifique as informações da classe existente.'
                : 'Crie uma nova classe para sua campanha.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Ícone */}
            <div className="space-y-2">
              <Label htmlFor="icon">Ícone</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="text-2xl w-16 h-16"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                >
                  {formData.icon}
                </Button>
                <Input
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="Cole um emoji aqui"
                  className="flex-1"
                />
              </div>
              
              {showEmojiPicker && (
                <div className="grid grid-cols-8 gap-1 p-2 border rounded-md bg-background">
                  {COMMON_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="text-2xl p-2 hover:bg-accent rounded transition-colors"
                      onClick={() => {
                        setFormData({ ...formData, icon: emoji })
                        setShowEmojiPicker(false)
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Classe *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Guerreiro, Mago, Cientista..."
                required
              />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva as características desta classe..."
                rows={3}
                required
              />
            </div>

            {/* Pontos Iniciais */}
            <div className="space-y-2">
              <Label htmlFor="starting_points">Pontos Iniciais *</Label>
              <Input
                id="starting_points"
                type="number"
                min="0"
                max="100"
                value={formData.starting_points}
                onChange={(e) => setFormData({ ...formData, starting_points: parseInt(e.target.value) || 0 })}
                required
              />
              <p className="text-xs text-muted-foreground">
                Pontos disponíveis para distribuir nos atributos do personagem
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <CircleNotch size={16} className="mr-2 animate-spin" />}
              {isEditing ? 'Salvar Alterações' : 'Criar Classe'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
